import React, { useCallback, useEffect, useRef, useState } from 'react';
import { uploadDocumenti, documentiUpload, riprovaDocumento, downloadDocumento } from '../api';

// ─────────────────────────────────────────────────────────────────────────────
// CaricaDocumenti — card riusabile per caricare documenti
//   tipo="ricorso"  id={gestione_ricorso.id}    → clienti/CF/ricorsi/CODICE/
//   tipo="cliente"  id={cliente.codice_cliente} → clienti/CF/documenti/
// L'upload risponde subito; l'indicizzazione (OCR, embedding) gira in background
// e la lista si aggiorna da sola finché ci sono documenti in coda.
// ─────────────────────────────────────────────────────────────────────────────

const ESTENSIONI = ['.pdf', '.docx', '.doc', '.txt', '.eml', '.xml', '.p7m'];
const POLLING_MS = 5000;
const ACCENTO = '#2B6CB0';       // azzurro: distinto dal verde dei collegati e dal viola della sentenza

const STATI = {
  in_coda:     { label: 'In coda',         bg: '#EDF2F7', fg: '#4A5568' },
  in_corso:    { label: 'In elaborazione', bg: '#EBF8FF', fg: '#2B6CB0' },
  indicizzato: { label: 'Indicizzato',     bg: '#F0FFF4', fg: '#276749' },
  errore:      { label: 'Errore',          bg: '#FFF5F5', fg: '#C53030' },
};

const btnPiccolo = (colore, pieno = false) => ({
  padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
  border: `1px ${pieno ? 'solid' : 'dashed'} ${colore}`,
  background: pieno ? colore : 'white', color: pieno ? 'white' : colore,
  textTransform: 'none', letterSpacing: 'normal', fontWeight: 500,
});

function estensioneOk(nome) {
  const n = (nome || '').toLowerCase();
  return ESTENSIONI.some(e => n.endsWith(e));
}

function formattaKB(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Attraversa ricorsivamente le cartelle trascinate (API webkitGetAsEntry)
function leggiEntry(entry, prefisso) {
  return new Promise(resolve => {
    if (entry.isFile) {
      entry.file(file => resolve([{ file, percorso: prefisso }]), () => resolve([]));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const tutti = [];
      const leggi = () => reader.readEntries(async entries => {
        if (!entries.length) {
          const nested = await Promise.all(tutti.map(e => leggiEntry(e, prefisso ? `${prefisso}/${entry.name}` : entry.name)));
          resolve(nested.flat());
          return;
        }
        tutti.push(...entries);
        leggi();
      }, () => resolve([]));
      leggi();
    } else resolve([]);
  });
}

async function daDrop(dataTransfer) {
  const items = Array.from(dataTransfer.items || []);
  const entries = items.map(i => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null)).filter(Boolean);
  if (entries.length) {
    const liste = await Promise.all(entries.map(e => leggiEntry(e, '')));
    return liste.flat();
  }
  return Array.from(dataTransfer.files || []).map(file => ({ file, percorso: '' }));
}

function daInput(fileList) {
  return Array.from(fileList || []).map(file => {
    const rel = file.webkitRelativePath || '';
    const percorso = rel.includes('/') ? rel.slice(0, rel.lastIndexOf('/')) : '';
    return { file, percorso };
  });
}

// aderente (facoltativo): nei ricorsi collettivi carica e mostra il fascicolo personale di quel
// ricorrente; senza, sulla scheda collettiva il server usa il fascicolo processuale.
// sottocartellaPredefinita (facoltativa): proposta per ogni file aggiunto, davanti alle sue
// eventuali sottocartelle; resta modificabile file per file.
export default function CaricaDocumenti({ tipo, id, titolo = 'Documenti', aderente, sottocartellaPredefinita = '' }) {
  const [docs, setDocs] = useState([]);
  const [pendenti, setPendenti] = useState(0);
  const [caricamento, setCaricamento] = useState(false);
  const [selezione, setSelezione] = useState([]);       // [{file, percorso}]
  const [inviando, setInviando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [esito, setEsito] = useState(null);               // {caricati, scartati}
  const [errore, setErrore] = useState('');
  const [trascinando, setTrascinando] = useState(false);
  const inputFile = useRef(null);
  const inputCartella = useRef(null);

  const carica = useCallback(async () => {
    if (!id) return;
    try {
      setCaricamento(true);
      const res = await documentiUpload(tipo, id, aderente);
      setDocs(res.data.documenti || []);
      setPendenti(res.data.pendenti || 0);
    } catch (e) {
      console.error('Errore lista documenti:', e);
    } finally {
      setCaricamento(false);
    }
  }, [tipo, id, aderente]);

  useEffect(() => { carica(); }, [carica]);

  // Se un file cade fuori dall'area di trascinamento, il browser non deve aprirlo
  useEffect(() => {
    const blocca = e => { e.preventDefault(); };
    window.addEventListener('dragover', blocca);
    window.addEventListener('drop', blocca);
    return () => { window.removeEventListener('dragover', blocca); window.removeEventListener('drop', blocca); };
  }, []);

  // Polling finché ci sono documenti in coda/elaborazione
  useEffect(() => {
    if (!pendenti) return undefined;
    const t = setInterval(carica, POLLING_MS);
    return () => clearInterval(t);
  }, [pendenti, carica]);

  const aggiungi = (letti) => {
    setEsito(null);
    setErrore('');
    const base = sottocartellaPredefinita.trim();
    const nuovi = base ? letti.map(n => ({ ...n, percorso: n.percorso ? `${base}/${n.percorso}` : base })) : letti;
    setSelezione(prev => {
      const chiavi = new Set(prev.map(s => `${s.percorso}/${s.file.name}`));
      return [...prev, ...nuovi.filter(n => !chiavi.has(`${n.percorso}/${n.file.name}`))];
    });
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setTrascinando(false);
    aggiungi(await daDrop(e.dataTransfer));
  };

  const invia = async () => {
    if (!selezione.length) return;
    setInviando(true);
    setProgresso(0);
    setErrore('');
    try {
      const res = await uploadDocumenti(
        tipo, id,
        selezione.map(s => s.file),
        selezione.map(s => s.percorso),
        ev => { if (ev.total) setProgresso(Math.round((ev.loaded * 100) / ev.total)); },
        aderente
      );
      setEsito(res.data);
      setSelezione([]);
      await carica();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErrore(typeof d === 'string' ? d : (e.message || 'Caricamento non riuscito'));
    } finally {
      setInviando(false);
    }
  };

  const riprova = async (docId) => {
    try {
      await riprovaDocumento(docId);
      await carica();
    } catch (e) {
      alert(e.response?.data?.detail || 'Impossibile rimettere in coda il documento');
    }
  };

  const nonValidi = selezione.filter(s => !estensioneOk(s.file.name)).length;
  const totaleBytes = selezione.reduce((a, s) => a + (s.file.size || 0), 0);

  // Raggruppo i documenti per sottocartella
  const gruppi = docs.reduce((acc, d) => {
    const k = d.sottocartella || '';
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});
  const chiaviGruppi = Object.keys(gruppi).sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));

  return (
    <div style={{ marginTop: 18, padding: '12px 16px 14px', borderRadius: 8,
      background: '#F3F7FB', border: '1px solid #D9E4F0' }}>
      {/* Intestazione — stesso stile di "Ricorsi collegati" */}
      <div style={{ fontSize: 12, fontWeight: 600, color: ACCENTO, paddingBottom: 6,
        borderBottom: `2px solid ${ACCENTO}25`, display: 'flex',
        justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {titolo}
          {docs.length > 0 && <span style={{ color: '#718096', fontWeight: 500 }}> · {docs.length}</span>}
          {pendenti > 0 && <span style={{ color: '#2B6CB0', fontWeight: 500 }}> · {pendenti} in elaborazione</span>}
        </span>
        <span style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => inputFile.current?.click()} disabled={inviando} style={btnPiccolo(ACCENTO)}>📄 Aggiungi file</button>
          <button onClick={() => inputCartella.current?.click()} disabled={inviando} style={btnPiccolo(ACCENTO)}>📁 Aggiungi cartella</button>
        </span>
      </div>
      <input ref={inputFile} type="file" multiple style={{ display: 'none' }}
        onChange={e => { aggiungi(daInput(e.target.files)); e.target.value = ''; }} />
      <input ref={inputCartella} type="file" webkitdirectory="" directory="" multiple style={{ display: 'none' }}
        onChange={e => { aggiungi(daInput(e.target.files)); e.target.value = ''; }} />

      {/* Area di trascinamento */}
      <div
        onDragOver={e => { e.preventDefault(); if (!trascinando) setTrascinando(true); }}
        onDragLeave={() => setTrascinando(false)}
        onDrop={onDrop}
        style={{ marginTop: 10, padding: selezione.length ? 10 : 18, minHeight: selezione.length ? undefined : 96,
          display: selezione.length ? 'block' : 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8, textAlign: 'center',
          border: `2px dashed ${trascinando ? ACCENTO : '#C3D3E6'}`,
          background: trascinando ? '#E3EEFB' : '#FFFFFF', color: '#718096', fontSize: 12,
          transition: 'background .15s, border-color .15s' }}>
        {selezione.length === 0
          ? <span>Trascina qui i file o una cartella intera (le sottocartelle, es. <b>NOTIFICA</b>, vengono mantenute)
              {sottocartellaPredefinita.trim() && <><br />Sottocartella proposta: <b data-testid="sottocartella-predefinita">
                {sottocartellaPredefinita.trim()}</b></>}</span>
          : (
            <div style={{ textAlign: 'left' }}>
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {selezione.map((s, i) => {
                  const ok = estensioneOk(s.file.name);
                  return (
                    <div key={`${s.percorso}/${s.file.name}`} style={{ display: 'flex', alignItems: 'center', gap: 8,
                      padding: '4px 6px', borderBottom: '1px solid #EDF2F7', color: ok ? '#1A1A2E' : '#C53030' }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={s.file.name}>{s.file.name}</span>
                      <input value={s.percorso} placeholder="sottocartella (opz.)" disabled={inviando}
                        onChange={e => setSelezione(prev => prev.map((p, j) => j === i ? { ...p, percorso: e.target.value } : p))}
                        style={{ width: 150, fontSize: 11, padding: '2px 6px', border: '1px solid #E2E8F0', borderRadius: 4 }} />
                      <span style={{ width: 60, textAlign: 'right', color: '#A0AEC0' }}>{formattaKB(s.file.size)}</span>
                      {!ok && <span title="Formato non supportato">⚠</span>}
                      <button onClick={() => setSelezione(prev => prev.filter((_, j) => j !== i))} disabled={inviando}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#A0AEC0', fontSize: 14, padding: '0 4px' }}
                        title="Rimuovi">×</button>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <span style={{ flex: 1 }}>
                  {selezione.length} file · {formattaKB(totaleBytes)}
                  {nonValidi > 0 && <span style={{ color: '#C53030' }}> · {nonValidi} con formato non supportato (verranno scartati)</span>}
                </span>
                {inviando && (
                  <span style={{ width: 140, height: 6, background: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
                    <span style={{ display: 'block', width: `${progresso}%`, height: '100%', background: ACCENTO, transition: 'width .2s' }} />
                  </span>
                )}
                <button onClick={() => setSelezione([])} disabled={inviando}
                  style={{ ...btnPiccolo('#A0AEC0'), border: '1px solid #CBD5E0', color: '#4A5568' }}>Annulla</button>
                <button onClick={invia} disabled={inviando || selezione.length === nonValidi} style={btnPiccolo(ACCENTO, true)}>
                  {inviando ? `Caricamento ${progresso}%` : `Carica ${selezione.length - nonValidi} file`}
                </button>
              </div>
            </div>
          )}
      </div>

      {/* Esito ultimo caricamento */}
      {esito && (
        <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, fontSize: 12,
          background: esito.n_scartati ? '#FFFBEB' : '#F0FFF4', color: esito.n_scartati ? '#975A16' : '#276749' }}>
          {esito.n_caricati} file caricati, indicizzazione in corso.
          {esito.n_scartati > 0 && (
            <span> {esito.n_scartati} scartati: {esito.scartati.map(s => `${s.file} (${s.motivo})`).join('; ')}</span>
          )}
        </div>
      )}
      {errore && (
        <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, fontSize: 12, background: '#FFF5F5', color: '#C53030' }}>
          {errore}
        </div>
      )}

      {/* Elenco documenti del fascicolo */}
      <div style={{ marginTop: 10 }}>
        {caricamento && docs.length === 0 && <div style={{ fontSize: 12, color: '#A0AEC0' }}>Caricamento…</div>}
        {!caricamento && docs.length === 0 && (
          <div style={{ fontSize: 12, color: '#A0AEC0' }}>Nessun documento nel fascicolo.</div>
        )}
        {chiaviGruppi.map(k => (
          <div key={k || '__radice'} style={{ marginBottom: 6 }}>
            {k && <div style={{ fontSize: 11, fontWeight: 600, color: '#4A5568', padding: '6px 0 2px' }}>📁 {k}</div>}
            {gruppi[k].map(d => {
              const st = STATI[d.stato] || STATI.indicizzato;
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px',
                  borderBottom: '1px solid #E2EAF3', fontSize: 12 }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1A1A2E' }}
                    title={d.percorso}>{d.nome_file}</span>
                  {d.stato === 'indicizzato' && d.num_chunks > 0 && (
                    <span style={{ color: '#A0AEC0', fontSize: 11 }}>{d.num_chunks} blocchi</span>
                  )}
                  <span title={d.errore || st.label}
                    style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: st.bg, color: st.fg,
                      cursor: d.errore ? 'help' : 'default' }}>
                    {d.stato === 'in_corso' && <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3,
                      background: st.fg, marginRight: 5, verticalAlign: 'middle' }} />}
                    {st.label}
                  </span>
                  {d.stato === 'errore' && (
                    <button onClick={() => riprova(d.id)} style={btnPiccolo('#C53030')} title={d.errore || ''}>↻ Riprova</button>
                  )}
                  <button onClick={() => downloadDocumento(d.id, d.nome_file)}
                    style={{ ...btnPiccolo('#4A5568'), border: '1px solid #CBD5E0' }}>⬇ Scarica</button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
